import { useState } from 'react';
import {
  FileText, Sparkles, HelpCircle,
  ShieldCheck
} from 'lucide-react';
import { Badge } from '@/shared/components/ui/Badge';
import { Button } from '@/shared/components/ui/Button';
import { Modal } from '@/shared/components/ui/Modal';
import { type CatalogEntry } from '../api';

interface TemplateStudioHeaderProps {
  catalog: CatalogEntry[] | undefined;
  isLoading?: boolean;
  activeDepartment: string;
}

export function TemplateStudioHeader({
  catalog,
  isLoading = false,
  activeDepartment,
}: TemplateStudioHeaderProps) {
  const [guideOpen, setGuideOpen] = useState(false);

  // Compute stats across the catalog
  const totalTemplates = catalog?.length || 0;
  const customCount = catalog?.reduce((acc, item) => {
    const hasCustom = item.customTemplates.some(
      (t) => (t.department || '') === activeDepartment
    );
    return acc + (hasCustom ? 1 : 0);
  }, 0) || 0;
  const publishedCount = catalog?.reduce((acc, item) => {
    const pub = item.customTemplates.some(
      (t) => (t.department || '') === activeDepartment && t.status === 'published'
    );
    return acc + (pub ? 1 : 0);
  }, 0) || 0;

  return (
    <div className="relative overflow-hidden rounded-2xl border border-outline bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 p-6 text-white shadow-xl">
      {/* Decorative subtle background gradients */}
      <div className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute right-1/3 -bottom-20 h-48 w-48 rounded-full bg-indigo-500/10 blur-2xl" />

      <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        {/* Left: Branding & Studio Title */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-medium text-indigo-200 backdrop-blur-md border border-white/10">
              <Sparkles className="h-3.5 w-3.5 text-indigo-400 animate-pulse" />
              <span>Enterprise Document Architecture</span>
            </span>
            <Badge tone="info" className="bg-indigo-500/20 text-indigo-300 border border-indigo-400/30">
              HR Module • ZYNX Verified
            </Badge>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-white backdrop-blur-md border border-white/20 shadow-inner">
              <FileText className="h-6 w-6 text-indigo-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-white">
                HR Document Templates Studio
              </h1>
              <p className="text-sm text-slate-300">
                Design, customize, and publish legally compliant offer letters, NDAs, and appointment contracts.
              </p>
            </div>
          </div>
        </div>

        {/* Right: Quick actions & Stats Bar */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
          {/* Quick Stats Pill */}
          <div className="flex items-center gap-4 rounded-xl bg-white/5 px-4 py-2.5 backdrop-blur-md border border-white/10">
            <div className="text-center">
              <div className="text-xs text-slate-400">Total Types</div>
              <div className="text-lg font-bold text-white">
                {isLoading ? '—' : totalTemplates}
              </div>
            </div>
            <div className="h-7 w-px bg-white/10" />
            <div className="text-center">
              <div className="text-xs text-slate-400">Customized</div>
              <div className="text-lg font-bold text-indigo-300">
                {isLoading ? '—' : `${customCount} / ${totalTemplates}`}
              </div>
            </div>
            <div className="h-7 w-px bg-white/10" />
            <div className="text-center">
              <div className="text-xs text-slate-400">Live Published</div>
              <div className="text-lg font-bold text-emerald-400">
                {isLoading ? '—' : publishedCount}
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setGuideOpen(true)}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20 text-xs font-medium shadow-xs"
              title="View token variable and letterhead guide"
            >
              <HelpCircle className="h-4 w-4 mr-1.5 text-indigo-300" />
              Token Guide
            </Button>

          </div>
        </div>
      </div>

      {/* Guide & Tokens Modal */}
      <Modal
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        title="Document Templates & Branding Guide"
        size="lg"
      >
        <div className="space-y-6 text-on-surface">
          <div className="rounded-xl bg-primary/5 p-4 border border-primary/20 flex items-start gap-3">
            <ShieldCheck className="h-6 w-6 text-primary shrink-0 mt-0.5" />
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-on-surface">
                Dynamic Letterhead & Legal Compliance
              </h4>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every HR document generated in ZYNX is automatically wrapped with your company’s active <strong>Letterhead Banner</strong>, <strong>Legal Entity Footer</strong>, and <strong>Authorized Signatory</strong> details configured in <strong>Settings &gt; HR &gt; Letterhead Branding</strong>.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-on-surface">How Variable Tokens Work</h4>
            <p className="text-xs text-muted-foreground">
              Insert Handlebars tokens like <code className="bg-surface-variant px-1.5 py-0.5 rounded text-primary font-mono">{`{{candidate_name}}`}</code> or <code className="bg-surface-variant px-1.5 py-0.5 rounded text-primary font-mono">{`{{salary_table_html}}`}</code> into your template body. When generating an offer or appointment letter, ZYNX replaces these tokens with live employee and payroll data.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
              <div className="rounded-lg border border-outline p-3 bg-surface">
                <div className="text-xs font-semibold text-on-surface mb-1">
                  1. Scope Hierarchy (Company vs Department)
                </div>
                <p className="text-xs text-muted-foreground">
                  You can publish a company-wide default template, or customize specific templates for individual departments (e.g. <em>Engineering</em> vs <em>Sales</em>).
                </p>
              </div>

              <div className="rounded-lg border border-outline p-3 bg-surface">
                <div className="text-xs font-semibold text-on-surface mb-1">
                  2. ZYNX Standard vs Custom Drafts
                </div>
                <p className="text-xs text-muted-foreground">
                  If you ever want to revert your customizations, click <strong>Reset to Standard</strong> to restore ZYNX’s legally vetted default template.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-2 border-t border-outline">
            <Button size="sm" variant="primary" onClick={() => setGuideOpen(false)}>
              Got it
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
